import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pin, PinOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { useShortcuts } from '../contexts/ShortcutsContext';

/**
 * Icon-only pin. With no `target` prop it pins wherever the user is
 * standing (the deepest registered location); pass `target` explicitly
 * from inside a full-screen dialog that covers the main button.
 */
const PinShortcutButton = ({ target: explicit, className = '' }) => {
  const { t } = useTranslation();
  const { currentTarget, findPinned, addShortcut, removeShortcut } = useShortcuts();
  const target = explicit || currentTarget;
  if (!target?.tab) return null;

  const pinned = findPinned(target);

  const handle = async () => {
    try {
      if (pinned) {
        await removeShortcut(pinned.id);
        toast.success(t('shortcuts.unpinned'));
      } else {
        await addShortcut(target);
        toast.success(t('shortcuts.pinned'));
      }
    } catch (e) {
      toast.error(e?.response?.status === 409 ? t('shortcuts.limit') : t('shortcuts.saveFailed'));
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={handle}
      title={pinned ? t('shortcuts.unpin') : `${t('shortcuts.pin')} — ${target.label}`}
      aria-label={pinned ? t('shortcuts.unpin') : t('shortcuts.pin')}
      className={`h-8 w-8 shrink-0 ${pinned ? 'text-amber-600 hover:text-amber-700' : 'text-slate-400 hover:text-slate-700'} ${className}`}
      data-testid="pin-shortcut-btn"
    >
      {pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
    </Button>
  );
};

export default PinShortcutButton;
